import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { PermissionsService } from '../../shared/services/permissions.service';
import { AuthService } from '../services/auth.service';
import { AlertController } from '../../shared/services/alert-controller.service';

/**
 * Guard that allows access only if the user has the permission required by the route.
 * The required permission is read from route.data['permission'].
 * If the user does not have permission, redirects to the first route they are allowed to access.
 * If the user has no allowed routes, shows toast, clears session and redirects to login.
 */
@Injectable({ providedIn: 'root' })
export class PermissionGuard implements CanActivate {

  constructor(
    private permissions: PermissionsService,
    private router: Router,
    private auth: AuthService,
    private alert: AlertController
  ) {}

  canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean | UrlTree {
    const requiredPermission = route.data['permission'] as string | undefined;

    if (requiredPermission && this.permissions.isPermission(requiredPermission)) {
      return true;
    }

    const firstAllowedPath = this.permissions.getFirstAllowedRoutePath();
    if (firstAllowedPath) {
      return this.router.createUrlTree([firstAllowedPath]);
    }
    this.alert.showToast({
      summary: 'Access Restricted',
      message: 'You don’t currently have permission to access this application. Please contact your administrator to request access or enable the required permissions.',
      severity: 'error'
    });
    this.auth.logout();
    return false;
  }
}
